import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { resetPassword, confirmResetPassword } from 'aws-amplify/auth';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import logoSrc from '../img/SB-logo01.jpg';

type Step = 'request' | 'confirm' | 'done';

export function ResetPasswordPage() {
  useDocumentTitle('Passwort zurücksetzen – Symbolbuch.de');
  const [step, setStep] = useState<Step>('request');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleRequestCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await resetPassword({ username: email });
      setStep('confirm');
    } catch (err: any) {
      const msg = err.message ?? '';
      if (msg.includes('UserNotFoundException') || msg.includes('not found')) {
        setError('Es wurde kein Konto mit dieser E-Mail-Adresse gefunden.');
      } else if (msg.includes('LimitExceededException') || msg.includes('limit')) {
        setError('Zu viele Versuche. Bitte versuchen Sie es später erneut.');
      } else {
        setError('Fehler beim Senden des Codes. Bitte versuchen Sie es erneut.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await confirmResetPassword({
        username: email,
        confirmationCode: code,
        newPassword,
      });
      setStep('done');
    } catch (err: any) {
      const msg = err.message ?? '';
      if (msg.includes('CodeMismatchException') || msg.includes('code')) {
        setError('Der eingegebene Code ist ungültig. Bitte prüfen Sie Ihre E-Mail.');
      } else if (msg.includes('InvalidPasswordException') || msg.includes('password')) {
        setError('Das Passwort erfüllt nicht die Anforderungen (mind. 8 Zeichen).');
      } else if (msg.includes('ExpiredCodeException') || msg.includes('expired')) {
        setError('Der Code ist abgelaufen. Bitte fordern Sie einen neuen Code an.');
      } else {
        setError('Fehler beim Zurücksetzen. Bitte versuchen Sie es erneut.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 py-12">
      <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-200 w-full max-w-md">
        <div className="text-center mb-8">
          <img src={logoSrc} alt="Symbolbuch.de Logo" className="w-20 h-20 mx-auto mb-4 rounded-2xl object-contain" />
          <h1 className="text-2xl font-bold text-gray-900">Passwort zurücksetzen</h1>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm mb-4">{error}</div>
        )}

        {step === 'request' && (
          <form onSubmit={handleRequestCode} className="space-y-4">
            <p className="text-sm text-gray-600">
              Geben Sie Ihre E-Mail-Adresse ein. Sie erhalten einen Bestätigungscode per E-Mail.
            </p>
            <div>
              <label htmlFor="reset-email" className="block text-sm font-medium text-gray-700 mb-1">
                E-Mail
              </label>
              <input
                id="reset-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-gray-900 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-shadow"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-indigo-600 text-white py-3 rounded-lg font-semibold hover:bg-indigo-700 disabled:opacity-50 transition-colors shadow-sm"
            >
              {loading ? 'Wird gesendet…' : 'Code senden'}
            </button>
          </form>
        )}

        {step === 'confirm' && (
          <form onSubmit={handleConfirmReset} className="space-y-4">
            <p className="text-sm text-gray-600">
              Ein Bestätigungscode wurde an <span className="font-semibold">{email}</span> gesendet.
              Geben Sie den Code und Ihr neues Passwort ein.
            </p>
            <div>
              <label htmlFor="reset-code" className="block text-sm font-medium text-gray-700 mb-1">
                Bestätigungscode
              </label>
              <input
                id="reset-code"
                type="text"
                inputMode="numeric"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                required
                autoFocus
                autoComplete="one-time-code"
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-gray-900 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-shadow tracking-widest text-center text-lg"
              />
            </div>
            <div>
              <label htmlFor="reset-new-password" className="block text-sm font-medium text-gray-700 mb-1">
                Neues Passwort
              </label>
              <input
                id="reset-new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={8}
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-gray-900 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-shadow"
              />
              <p className="text-xs text-gray-400 mt-1">Mindestens 8 Zeichen</p>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-indigo-600 text-white py-3 rounded-lg font-semibold hover:bg-indigo-700 disabled:opacity-50 transition-colors shadow-sm"
            >
              {loading ? 'Wird zurückgesetzt…' : 'Passwort zurücksetzen'}
            </button>
            <button
              type="button"
              onClick={() => { setStep('request'); setCode(''); setNewPassword(''); setError(''); }}
              className="w-full text-sm text-gray-500 hover:text-gray-700"
            >
              Neuen Code anfordern
            </button>
          </form>
        )}

        {step === 'done' && (
          <div className="text-center space-y-4">
            <div className="w-16 h-16 mx-auto bg-emerald-50 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="text-gray-900 font-semibold">Passwort erfolgreich geändert!</p>
            <p className="text-sm text-gray-600">
              Sie können sich jetzt mit Ihrem neuen Passwort anmelden.
            </p>
            <button
              onClick={() => navigate('/login')}
              className="w-full bg-indigo-600 text-white py-3 rounded-lg font-semibold hover:bg-indigo-700 transition-colors shadow-sm"
            >
              Zur Anmeldung
            </button>
          </div>
        )}

        {step !== 'done' && (
          <div className="mt-6 text-center">
            <Link to="/login" className="text-sm text-indigo-600 hover:text-indigo-700 font-medium">
              Zurück zur Anmeldung
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
